import { storeToRefs } from 'pinia'

import { PosthogFeatureFlags, type PosthogFeatureFlagKey } from '@/lib/posthog'
import { useAppStore } from '@/store/app'
import retentionImage from '@/views/components/modals/assets/retentionModalHeader.webp'
import welcomeImage from '@/views/components/modals/assets/welcomeModalHeader.svg'

import type { IIcons } from '@foundever/ui-lib'

/**
 * The optional "remind me / don't remind me again" checkbox in the actions bar.
 * Its state decides how long the dismissal cookie lives — hence how soon the
 * modal is allowed to reappear.
 */
export interface AnnouncementModalReminder {
  checkboxLabel: string
  daysWhenChecked: number
  daysWhenUnchecked: number
}

export interface AnnouncementModalConfig {
  id: string
  ariaLabel: string
  title: string
  /**
   * Body copy, one entry per paragraph. Rendered with `v-html` so static copy can
   * carry inline markup (e.g. `<strong>`); values are author-controlled, never user input.
   */
  paragraphs: string[]
  image: string
  imageAlt: string
  /**
   * Single primary button that confirms + closes the modal. `icon` is ui-lib's
   * own union because that is what renders it — the modal passes it straight to
   * `UIIcon`, so typing it against the local icon set only deferred the mismatch
   * to a cast at the boundary.
   */
  primaryButton: {
    label: string
    icon?: IIcons
  }
  reminder?: AnnouncementModalReminder
  /**
   * Once this cookie is present the modal is suppressed; it is (re)written when the
   * user closes the modal, with an expiry derived from the reminder checkbox.
   */
  dismissCookie: string
  posthogFeatureFlag?: PosthogFeatureFlagKey
  /** Extra runtime gate on top of the cookie + feature flag. */
  isEligible?: () => boolean
}

export function getAnnouncementModalConfigs(): AnnouncementModalConfig[] {
  const { isUserAuthorisedForEverGpt } = storeToRefs(useAppStore())

  return [
    {
      id: 'data-retention',
      ariaLabel: 'Conversation retention notice',
      title: 'A quick note about conversation retention',
      paragraphs: [
        'To meet our privacy requirements, <strong>conversations inactive for 30 days</strong> are automatically <strong>deleted</strong>. You can continue the discussion to keep it active.',
      ],
      image: retentionImage,
      imageAlt: 'Illustration representing conversation data retention',
      primaryButton: { label: 'Got it' },
      reminder: {
        checkboxLabel: "Got it. Don't remind me again.",
        // "Don't remind me again" — suppress effectively forever.
        daysWhenChecked: 3650,
        // Otherwise re-show the reminder after a week.
        daysWhenUnchecked: 7,
      },
      dismissCookie: 'hasSeenRetentionNotice',
      posthogFeatureFlag: PosthogFeatureFlags.DataRetentionModal,
      isEligible: () => isUserAuthorisedForEverGpt.value,
    },
    {
      id: 'welcome',
      ariaLabel: 'Welcome to EverGPT',
      title: 'Welcome to EverGPT!',
      paragraphs: [
        'EverGPT is your smart assistant, here to help you write, solve problems, and get things done with ease. Whether you need fresh ideas, quick answers, or a boost in productivity, EverGPT is ready for you.',
        '<strong>Knowledge, creativity, and efficiency—right at your fingertips!</strong> Get started now and discover a faster, smarter way to work.',
      ],
      image: welcomeImage,
      imageAlt: 'Welcome to EverGPT illustration',
      primaryButton: { label: "Okay, let's go", icon: 'arrowRightRegular' },
      reminder: {
        checkboxLabel: 'Remind me in 30 days',
        daysWhenChecked: 30,
        daysWhenUnchecked: 1,
      },
      dismissCookie: 'hasShownWelcomeModal',
      // Kept disabled: the welcome modal is not currently shown. Flip this to
      // re-introduce it (an explicit `false` avoids the "unconfigured PostHog flag
      // defaults to on when PostHog isn't loaded" trap in `isPosthogFlagOn`).
      isEligible: () => false,
    },
  ]
}
